import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckoutSteps } from "@/components/billing/CheckoutSteps";
import { db, money, type PackageRecord } from "@/lib/billing";
import { formatMoney, planMonthlyCents, type PlanPriceSlug } from "@/lib/currency";
import { useLocale, intlLocale, type Locale } from "@/lib/locale";
import { useAuth } from "@/hooks/useAuth";

/** Version stamp persisted to user_consents.version. Bump on legal doc changes. */
const CHECKOUT_TERMS_VERSION = "2026-05-13";

async function fetchPackage(packageId: string): Promise<PackageRecord | null> {
  const { data, error } = await db.from("packages").select("*").eq("id", packageId).maybeSingle();
  if (error) throw error;
  return data;
}

async function functionErrorMessage(error: any, fallback: string, functionUnavailable: string) {
  const context = error?.context;
  if (context instanceof Response) {
    const body = await context.clone().json().catch(() => null);
    if (body?.error) return body.error as string;
  }

  const message = error?.message ?? fallback;
  if (message.includes("Failed to send a request")) {
    return functionUnavailable;
  }
  return message;
}

/** Price shown for a package: the visitor's currency when the plan is in the list, else the package's own. */
function displayPrice(pkg: PackageRecord, uiLocale: Locale, locale: string): string {
  const base = pkg.slug.replace(/-v2$/, "") as PlanPriceSlug;
  const cents = (["starter", "growth", "network", "agency", "enterprise"] as string[]).includes(base) ? planMonthlyCents(base) : null;
  return cents ? formatMoney(cents, uiLocale) : money(pkg.monthly_price, pkg.currency, false, locale);
}

export default function CheckoutReviewPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [packageId, setPackageId] = useState(params.get("package") ?? "");

  useEffect(() => {
    if (packageId) return;
    const saved = JSON.parse(sessionStorage.getItem("delapresse_checkout") || "{}");
    if (saved.packageId) setPackageId(saved.packageId);
  }, [packageId]);

  const { data: pkg, isLoading } = useQuery({
    queryKey: ["checkout-review-package", packageId],
    queryFn: () => fetchPackage(packageId),
    enabled: !!packageId,
  });

  const tax = useMemo(() => (pkg?.monthly_price ?? 0) * 0, [pkg]);
  const total = (pkg?.monthly_price ?? 0) + tax;
  const stripeReady = Boolean(pkg?.stripe_price_id || (pkg && pkg.monthly_price > 0));
  const uiLocale = useLocale();
  const locale = intlLocale(uiLocale);
  const [termsAccepted, setTermsAccepted] = useState(false);

  /**
   * Persist the checkout-side terms acceptance. Fire-and-forget — the user
   * proceeds to Stripe even if the row write fails, but we want the audit
   * trail when it does land.
   */
  const recordCheckoutConsent = async () => {
    if (!user) return;
    await db.from("user_consents").insert({
      user_id: user.id,
      consent_type: "checkout_terms",
      version: CHECKOUT_TERMS_VERSION,
      user_agent: navigator.userAgent.slice(0, 500),
    });
  };

  const checkout = useMutation({
    mutationFn: async () => {
      if (!pkg) throw new Error("Package missing");
      if (!pkg.stripe_price_id && pkg.monthly_price <= 0) {
        throw new Error(t("checkout.review.noStripePrice"));
      }
      const { data, error } = await db.functions.invoke("create-checkout-session", {
        body: {
          package_id: pkg.id,
          success_url: `${window.location.origin}/checkout/success?package=${pkg.id}&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/checkout/cancel?package=${pkg.id}`,
        },
      });
      if (error) throw new Error(await functionErrorMessage(
        error,
        t("checkout.review.stripeStartError"),
        t("checkout.review.checkoutFunctionUnavailable"),
      ));
      if (!data?.url) throw new Error(t("checkout.review.noCheckoutUrl"));
      return data.url as string;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err: any) => toast.error(err?.message ?? t("checkout.review.stripeStartError")),
  });

  const requestManualActivation = () => {
    if (!pkg) return;
    const saved = JSON.parse(sessionStorage.getItem("delapresse_checkout") || "{}");
    sessionStorage.setItem("delapresse_checkout", JSON.stringify({
      ...saved,
      packageId: pkg.id,
      manualActivation: {
        package_id: pkg.id,
        package_name: pkg.name,
        requested_at: new Date().toISOString(),
        reason: "missing_stripe_price_id",
      },
    }));
    toast.success(t("checkout.review.manualSaved"));
    navigate(`/checkout/success?package=${pkg.id}&mode=manual`);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold">{t("checkout.review.title")}</h1>
        <p className="text-xs text-muted-foreground">{t("checkout.review.subtitle")}</p>
      </header>
      <main className="mx-auto max-w-4xl space-y-6 p-6">
        <CheckoutSteps current={2} />
        {isLoading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : !pkg ? (
          <Card><CardContent className="p-6 text-sm text-destructive">{t("checkout.review.noPackage")}</CardContent></Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <Card>
              <CardHeader><CardTitle className="text-base">{t("checkout.review.confirmedInfo")}</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Row label={t("checkout.review.name")} value={user.user_metadata?.full_name ?? user.email ?? t("public.account")} />
                <Row label={t("checkout.review.email")} value={user.email ?? t("checkout.common.notAvailable")} />
                <Row label={t("checkout.review.phone")} value={user.user_metadata?.phone_number ?? t("checkout.common.notProvided")} />
                <Row label={t("checkout.review.package")} value={pkg.name} />
                <Row label={t("checkout.review.mainService")} value={t("checkout.review.newsSiteService")} />
                <Row label={t("checkout.review.period")} value={t("checkout.review.monthly")} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("checkout.review.total")}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Row label={t("checkout.review.subtotal")} value={displayPrice(pkg, uiLocale, locale)} />
                <Row label={t("checkout.review.taxes")} value={tax > 0 ? money(tax, pkg.currency, false, locale) : t("checkout.review.noTaxes")} />
                <div className="border-t border-border pt-3">
                  <Row label={t("checkout.review.total")} value={pkg.is_enterprise ? t("packages.custom") : displayPrice(pkg, uiLocale, locale)} strong />
                </div>
                {!pkg.is_trial && !pkg.is_enterprise && !stripeReady && (
                  <Alert>
                    <AlertTitle>{t("checkout.review.manualTitle")}</AlertTitle>
                    <AlertDescription>
                      {t("checkout.review.manualDescription")}
                    </AlertDescription>
                  </Alert>
                )}
                {!pkg.is_trial && !pkg.is_enterprise && (
                  <div className="flex items-start gap-2 pt-1">
                    <Checkbox
                      id="checkout-terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="checkout-terms" className="text-xs leading-5 text-muted-foreground">
                      J'accepte les{" "}
                      <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CGV</Link>{" "}
                      et la{" "}
                      <Link to="/politique-rgpd" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">politique de confidentialité</Link>.
                    </Label>
                  </div>
                )}
                {pkg.is_trial ? (
                  <Button className="w-full" onClick={() => navigate("/billing/subscription")}>{t("checkout.actions.activateTrial")}</Button>
                ) : pkg.is_enterprise ? (
                  <Button asChild className="w-full"><Link to="/contact">{t("checkout.actions.contactTeam")}</Link></Button>
                ) : stripeReady ? (
                  <Button
                    className="w-full"
                    onClick={() => {
                      void recordCheckoutConsent();
                      checkout.mutate();
                    }}
                    disabled={checkout.isPending || !stripeReady || !termsAccepted}
                  >
                    {checkout.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                    {t("checkout.actions.payWithStripe")}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => {
                      void recordCheckoutConsent();
                      requestManualActivation();
                    }}
                    disabled={!termsAccepted}
                  >
                    {t("checkout.actions.requestActivation")}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? "text-lg font-bold" : "font-medium"}>{value}</span>
    </div>
  );
}
